import { useCallback, useState } from "react";
import { API } from "aws-amplify";
import { Buffer } from 'buffer';
import { useLoginUser } from "../hooks/providers/useLoginUserProvider";
import { useUser } from "./useUser";
import { issueCurrentObj, getIssueListResult, issueDetailObj,
          getIssueGroupResult, getNoticeListResult, getCaseListResult,
          noticeDetailObj, caseDetailObj, issueDiffObj,
          getNoticeDetailParam,
          getIssueDiffParam, translateNoticeParam,
          searchParam } from "../types/issue";

/////////////////////////////////////////////////////////////////////////////
// 障害関連Lib
/////////////////////////////////////////////////////////////////////////////
export const useIssue = () => {
  const { loginUser } = useLoginUser();
  const [ executing, setExecuting ] = useState(false);
  const [ currentData, setCurrentData ] = useState<issueCurrentObj|null>(null);
  const [ getIssueListResult, setGetIssueListResult ] = useState<getIssueListResult|null>(null);
  const [ getIssueGroupResult, setGetIssueGroupResult ] = useState<getIssueGroupResult|null>(null);
  const [ issueDetailObj, setIssueDetailObj ] = useState<issueDetailObj|null>(null);
  const [ getNoticeListResult, setGetNoticeListResult ] = useState<getNoticeListResult|null>(null);
  const [ noticeDetailObj, setNoticeDetailObj ] = useState<noticeDetailObj|null>(null);
  const [ issueDiffObj, setIssueDiffObj ] = useState<issueDiffObj|null>(null);
  const [ updateIssueGroupResult, setUpdateIssueGroupResult ] = useState('');
  const [ getCaseListResult, setGetCaseListResult ] = useState<getCaseListResult|null>(null);
  const [ caseDetailObj, setCaseDetailObj ] = useState<caseDetailObj|null>(null);
  const [ issueGroupSummary, setIssueGroupSummary ] = useState('');
  const [ result, setResult ] = useState<string>('');
  
  const [ errObj, setErrObj ] = useState(null);
  const [ errMes ] = useState('');
  // const [ unitname, setUnitname ] = useState('');
  const { refreshToken } = useUser();
  
  /////////////////////////////////////////////////////////////////////////////
  // 現在状態の取得
  /////////////////////////////////////////////////////////////////////////////
  const getCurrentData = useCallback(async(obj:searchParam) => {
    setExecuting(true);

    const apiName = "BackendApi";
    const path = "/mypage/issue_current";
    const option = {
      headers: {
        'X-NecAuth-Token': await refreshToken()
      },
      body: {
        cloud_type: obj.cloud_type,
        bu_name: obj.buName,
        customer_name: obj.custName,
        blank_flag: obj.blankFlag,
      },
    };
    loginUser.envType === 'DEV' && console.log('getCurrentData_param',option);
    
    (async() => {
      await API.post(apiName, path, option)
      .then((res) => {
        loginUser.envType === 'DEV' && console.log('getCurrentData_res',res);
        setCurrentData(res);
      })
      .catch((err) => {
        setErrObj(err);
      })
      .finally(() => {
        setExecuting(false);
      });
    })();

  }, []);

  /////////////////////////////////////////////////////////////////////////////
  // 障害履歴取得
  /////////////////////////////////////////////////////////////////////////////
  const getIssueList = useCallback(async(obj:searchParam) => {
    setExecuting(true);
    const apiName = "BackendApi";
    const path = "/mypage/issue_history";
    const option = {
      headers: {
        'X-NecAuth-Token': await refreshToken()
      },
      body: {
        fail_from_date: obj.fromDate,
        fail_to_date: obj.toDate,
        cloud_type: obj.cloud_type,
        bu_name: obj.buName,
        fail_customer_name: obj.custName,
        blank_flag: obj.blankFlag,
        eval_key: obj.eval_key ,
      },
    };
    loginUser.envType === 'DEV' && console.log('getIssueList_param',option);
    
    (async() => {
      await API.post(apiName, path, option)
      .then((res) => {
        loginUser.envType === 'DEV' && console.log('getIssueList_res',res);
        setGetIssueListResult(res);
      })
      .catch((err) => {
        setErrObj(err);
      })
      .finally(() => {
        setExecuting(false);
      });
    })();

  }, []);

  /////////////////////////////////////////////////////////////////////////////
  // 通知一覧取得
  /////////////////////////////////////////////////////////////////////////////
  const getNoticeList = useCallback(async(obj:searchParam) => {
    setExecuting(true);

    const apiName = "BackendApi";
    const path = "/mypage/notice_mail_list";
    const option = {
      headers: {
        'X-NecAuth-Token': await refreshToken()
      },
      body: {
        from_date: obj.fromDate,
        to_date: obj.toDate,
        bu_name: obj.buName,
        customer_name: obj.custName,
        blank_flag: obj.blankFlag,
        class_name: obj.noticeKind,
        cloud_type: obj.cloud_type,
        eval_key: obj.eval_key,
      },
    };
    loginUser.envType === 'DEV' && console.log('getNoticeList_param',option);
    
    (async() => {
      await API.post(apiName, path, option)
      .then((res) => {
        loginUser.envType === 'DEV' && console.log('getNoticeList_res',res);
        setGetNoticeListResult(res);
      })
      .catch((err) => {
        setErrObj(err);
      })
      .finally(() => {
        setExecuting(false);
      });
    })();
    
  }, []);

  /////////////////////////////////////////////////////////////////////////////
  // 通知詳細
  /////////////////////////////////////////////////////////////////////////////
  const getNoticeDetail = useCallback(async(obj:getNoticeDetailParam) => {
    setExecuting(true);

    const apiName = "BackendApi";
    const path = "/mypage/notice_mail_detail";
    const option = {
      headers: {
        'X-NecAuth-Token': await refreshToken()
      },
      body: {
        mail_group_id: obj.mail_group_id,
        mail_file_key: obj.mail_file_key,
      },
    };
    loginUser.envType === 'DEV' && console.log('getNoticeDetail_param',option);
    
    (async() => {
      await API.post(apiName, path, option)
      .then((res) => {
        loginUser.envType === 'DEV' && console.log('getNoticeDetail_res',res);
        setNoticeDetailObj(res);
      })
      .catch((err) => {
        setErrObj(err);
      })
      .finally(() => {
        setExecuting(false);
      });
    })();

  }, []);

  /////////////////////////////////////////////////////////////////////////////
  // 通知翻訳
  /////////////////////////////////////////////////////////////////////////////
  const translateNotice = useCallback(async(obj:translateNoticeParam) => {
    setExecuting(true);

    const apiName = "BackendApi";
    const path = "/mypage/notice_mail_translate";
    const option = {
      headers: {
        'X-NecAuth-Token': await refreshToken()
      },
      body: {
        mail_group_id: obj.mail_group_id,
        mail_file_key: obj.mail_file_key,
      },
    };
    loginUser.envType === 'DEV' && console.log('translateNotice_param',option);
    
    (async() => {
      await API.post(apiName, path, option)
      .then((res) => {
        loginUser.envType === 'DEV' && console.log('translateNotice_res',res);
        setNoticeDetailObj(res);
      })
      .catch((err) => {
        setErrObj(err);
      })
      .finally(() => {
        setExecuting(false);
      });
    })();
    
  }, []);
  
  /////////////////////////////////////////////////////////////////////////////
  // 障害グループ詳細
  /////////////////////////////////////////////////////////////////////////////
  const getIssueGroup = useCallback(async(group_name: string, health_arn?: string) => {
    setExecuting(true);

    const apiName = "BackendApi";
    const path = "/issue/issue_summary";
    let option = {};
    if( health_arn ){
      option = {
        headers: {
          'X-NecAuth-Token': await refreshToken()
        },
        body: {
          health_arn: health_arn,
        },
      };
    }else{
      option = {
        headers: {
          'X-NecAuth-Token': await refreshToken()
        },
        body: {
          group_name_org: group_name,
        },
      };
    }
    loginUser.envType === 'DEV' && console.log('getIssueGroup_param',option);
    
    (async() => {
      await API.post(apiName, path, option)
      .then((res) => {
        loginUser.envType === 'DEV' && console.log('getIssueGroup_res',res);
        setGetIssueGroupResult(res);
      })
      .catch((err) => {
        setErrObj(err);
      })
      .finally(() => {
        setExecuting(false);
      });
    })();

  }, []);
  
  /////////////////////////////////////////////////////////////////////////////
  // 障害グループサマリ取得
  /////////////////////////////////////////////////////////////////////////////
  const getIssueGroupSummary = useCallback(async(group_name: string) => {
    setExecuting(true);

    const apiName = "BackendApi";
    const path = "/issue/issue_group_summary";
    const option = {
      headers: {
        'X-NecAuth-Token': await refreshToken()
      },
      body: {
        group_name_org: group_name,
      },
    };
    loginUser.envType === 'DEV' && console.log('getIssueGroupSummary_param',option);
    
    (async() => {
      await API.post(apiName, path, option)
      .then((res) => {
        loginUser.envType === 'DEV' && console.log('getIssueGroupSummary_res',res);
        setIssueGroupSummary(res.group_summary);
      })
      .catch((err) => {
        loginUser.envType === 'DEV' && console.log('getIssueGroupSummary_res',err);
        setErrObj(err);
      })
      .finally(() => {
        setExecuting(false);
      });
    })();

  }, []);
  
  /////////////////////////////////////////////////////////////////////////////
  // 障害詳細
  /////////////////////////////////////////////////////////////////////////////
  const getIssueDetail = useCallback(async(health_arn: string) => {
    setExecuting(true);

    const apiName = "BackendApi";
    const path = "/issue/issue_detail";
    const option = {
      headers: {
        'X-NecAuth-Token': await refreshToken()
      },
      body: {
        health_arn: health_arn,
      },
    };
    loginUser.envType  === 'DEV' && console.log('getIssueDetail_param',option);
    
    (async() => {
      await API.post(apiName, path, option)
      .then((res) => {
        loginUser.envType  === 'DEV' && console.log('getIssueDetail_res',res);
        setIssueDetailObj(res);
      })
      .catch((err) => {
        setErrObj(err);
      })
      .finally(() => {
        setExecuting(false);
      });
    })();

  }, []);
  
  /////////////////////////////////////////////////////////////////////////////
  // 差分取得
  /////////////////////////////////////////////////////////////////////////////
  const getIssueDiff = useCallback(async(param: getIssueDiffParam) => {
    setExecuting(true);

    const apiName = "BackendApi";
    const path = "/issue/issue_diff";
    const option = {
      headers: {
        'X-NecAuth-Token': await refreshToken()
      },
      body: {
        health_arn: param.health_arn,
        // payer_account_id: param.payer_account_id,
        req_type: param.req_Type,
        old_history: param.old_history,
        new_history: param.new_history,
      },
    };
    loginUser.envType === 'DEV' && console.log('getIssueDiff_option',option);

    (async() => {
      await API.post(apiName, path, option)
      .then((res) => {
        loginUser.envType === 'DEV' && console.log('getIssueDiff_res',res);
        setIssueDiffObj(res);
      })
      .catch((err) => {
        setErrObj(err);
      })
      .finally(() => {
        setExecuting(false);
      });
    })();

  }, []);

  /////////////////////////////////////////////////////////////////////////////
  // CASE一覧取得
  /////////////////////////////////////////////////////////////////////////////
  const getCaseList = useCallback(async(obj:searchParam) => {
    setExecuting(true);

    const apiName = "BackendApi";
    const path = "/mypage/issue_case_list";
    const option = {
      headers: {
        'X-NecAuth-Token': await refreshToken()
      },
      body: {
        case_from_date: obj.fromDate,
        case_to_date: obj.toDate,
        bu_name: obj.buName,
        case_customer_name: obj.custName,
        blank_flag: obj.blankFlag,
      },
    };
    loginUser.envType === 'DEV' && console.log('getCaseList_param',option);
    
    (async() => {
      await API.post(apiName, path, option)
      .then((res) => {
        loginUser.envType === 'DEV' && console.log('getCaseList_res',res);
        setGetCaseListResult(res);
      })
      .catch((err) => {
        setErrObj(err);
      })
      .finally(() => {
        setExecuting(false);
      });
    })();

  }, []);
  
  /////////////////////////////////////////////////////////////////////////////
  // CASE詳細取得
  /////////////////////////////////////////////////////////////////////////////
  const getCaseDetail = useCallback(async(customObjId:string) => {
    setExecuting(true);

    const apiName = "BackendApi";
    const path = "/mypage/issue_case_detail";
    const option = {
      headers: {
        'X-NecAuth-Token': await refreshToken()
      },
      body: {
        custom_obj_id: customObjId,
      },
    };
    loginUser.envType === 'DEV' && console.log('getCaseDetail_param',option);
    
    (async() => {
      await API.post(apiName, path, option)
      .then((res) => {
        loginUser.envType === 'DEV' && console.log('getCaseDetail_res',res);
        setCaseDetailObj(res);
      })
      .catch((err) => {
        setErrObj(err);
      })
      .finally(() => {
        setExecuting(false);
      });
    })();

  }, []);

  /////////////////////////////////////////////////////////////////////////////
  // 障害グループ名更新
  /////////////////////////////////////////////////////////////////////////////
  const updateIssueGroup = useCallback(async(group_name:string, group_id:string, updateItem:string,val:string) => {
    setExecuting(true);

    const apiName = "BackendApi";
    const path = "/issue/issue_summary_update";
    const option = {
      headers: {
        'X-NecAuth-Token': await refreshToken()
      },
      body: {
        group_name: group_name,
        group_id: group_id,
        another_name: updateItem === 'anotherName' ? val : undefined,
        comment: updateItem === 'comment' ? val : undefined,
      },
    };
    loginUser.envType === 'DEV' && console.log('updateIssueGroup_param',option);
    
    (async() => {
      await API.post(apiName, path, option)
      .then((res) => {
        loginUser.envType === 'DEV' && console.log('updateIssueGroup_res',res);
        setUpdateIssueGroupResult('succeed');
      })
      .catch((err) => {
        setErrObj(err);
      })
      .finally(() => {
        setExecuting(false);
      });
    })();

  }, []);
  
  ///////////////////////////////////////////////////////////////////
  // 報告書ダウンロード
  ///////////////////////////////////////////////////////////////////
  const DownloadReport = useCallback(async(s3_path: string, file_name: string) => {
    setExecuting(true);
    setResult('');
    const apiName = 'BackendApi';
    const path = '/download/report_download';
    
    const option = {
      headers: {
        'X-NecAuth-Token': await refreshToken()
      },
      queryStringParameters: {
        s3_path: s3_path,
      },
    };
    loginUser.envType === 'DEV' && console.log('reportDownload_param', s3_path, file_name);
  
    (async() => {
      await API.get(apiName, path, option)
      .then((res) => {
        // responseの先頭のbは除く
        const data = res.substring(1);
        const b64encode = Buffer.from(data, 'base64');
        
        // BOM付きUTF-8
        const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
        const blob = new Blob([bom, b64encode], {
          type: 'application/octet-stream',
        });
  
        // if (window.navigator.msSaveBlob) {
        //   // IEとEdge
        //   window.navigator.msSaveBlob(blob, file_name);
        // } else {
          // それ以外のブラウザ
          const anchor = document.createElement('a');
          const url = window.URL.createObjectURL(blob);
          document.body.appendChild(anchor);
          anchor.href = url;
          anchor.setAttribute('download', file_name);
          anchor.click();
          document.body.removeChild(anchor);
          
          loginUser.envType === 'DEV' && console.log('reportDownloadres', anchor);
          window.URL.revokeObjectURL(url);
        // }
          setResult('download');
      })
      .catch((err) => {
        setErrObj(err);
      })
      .finally(() => {
        setExecuting(false);
      });
    })();
  }, []);
  
  /////////////////////////////////////////////////////////////////////////////
  // 報告書削除
  /////////////////////////////////////////////////////////////////////////////
  const DeleteReport = useCallback(async(s3_path: string, group_name:string, group_id: string) => {
    setExecuting(true);

    const apiName = "BackendApi";
    const path = "/download/report_delete";
    const option = {
      headers: {
        'X-NecAuth-Token': await refreshToken()
      },
      body: {
        s3_path: s3_path,
        group_name: group_name,
        group_id: group_id,
      },
    };
    loginUser.envType === 'DEV' && console.log('reportDelete_param',option);
    
    (async() => {
      await API.post(apiName, path, option)
      .then((res) => {
        loginUser.envType === 'DEV' && console.log('reportDelete_res',res);
        setResult(res.result === 'succeed' ? 'delete' : '');
      })
      .catch((err) => {
        setErrObj(err);
      })
      .finally(() => {
        setExecuting(false);
      });
    })();

  }, []);
  
  /////////////////////////////////////////////////////////////////////////////
  // 報告書アップロード
  /////////////////////////////////////////////////////////////////////////////
  const UploadReport = useCallback(async(group_name:string, group_id:string, fileName: string, fileContext: string) => {
    setExecuting(true);
    
    const apiName = 'BackendApi';
    const path = '/download/report_upload';
    const option = {
      headers: {
        'X-NecAuth-Token': await refreshToken()
      },
      body: {
        group_name: group_name,
        group_id: group_id,
        file_name: fileName,
        file_body: fileContext,
      },
    };
    loginUser.envType === 'DEV' && console.log('UploadReport_param',option);
    
    (async() => {
      await API.post(apiName, path, option)
      .then((res) => {
        loginUser.envType === 'DEV' && console.log('UploadReporta_res',res);
        setResult(res.s3_path ? 'upload' : '');
      })
      .catch((err) => {
        setErrObj(err);
      })
      .finally(() => {
        setExecuting(false);
      });
    })();

  }, []);
  
  // ///////////////////////////////////////////////////////////////////
  // // 報告書アップロード-> 削除予定
  // ///////////////////////////////////////////////////////////////////
  // const UploadReport = useCallback(async(fileName: string, fileContext: string) => {
  //   setExecuting(true);
  //   const apiName = 'BackendApi';
  //   const path = '/download/report_upload';

  //   Auth.currentAuthenticatedUser()
  //     .then(async (user) => {
  //       // const token = user.signInUserSession.idToken.jwtToken;
  //       const option = {
  //         headers: {
  //           'X-NecAuth-Token': await refreshToken()
  //         },
  //         body: {
  //           file_name: fileName,
  //           file_body: fileContext,
  //         },
  //       };

  //       loginUser.envType === 'DEV' && console.log('UploadReport_param',option);

  //       const response = await API.post(apiName, path, option);

  //       loginUser.envType === 'DEV' && console.log('UploadReporta_param',response);
        
  //       const resTempPath = response.temp_path;

  //       // atachListとtempPathの更新
  //       const arr: Array<string> = [];
  //       updatePost.attach_list.forEach((obj) => {
  //         arr.push(obj.file_name);
  //         // アップロードしたファイルがattachListに存在する場合
  //         if (obj.file_name === fileName) {
  //           obj.mode = 'update';
  //           obj.temp_path = resTempPath;
  //         }
  //       });
  //       // アップロードしたファイルがattachListに存在しない場合
  //       if (!arr.includes(fileName)) {
  //         const newObj = {
  //           file_name: fileName,
  //           mode: 'create',
  //           s3_path: '',
  //           temp_path: resTempPath,
  //         };
  //         // attachList追加
  //         updatePost.attach_list.push(newObj);
  //       }
  //       setUpdatePost(updatePost);
  //       setTempPath(resTempPath);
  //       setErrMes('');
  //       // console.log(updatePost)
  //     })
  //     .catch((err) => {
  //       setErrObj(err);
  //     })
  //     .finally(() => {
  //       setExecuting(false);
  //     });
  //   },
  //   [updatePost]
  // );

  return {
    getCurrentData,
    getIssueList,
    getNoticeList,
    getNoticeDetail,
    translateNotice,
    getIssueDetail,
    getIssueDiff,
    getIssueGroup,
    getIssueGroupSummary,
    updateIssueGroup,
    DownloadReport,
    DeleteReport,
    UploadReport,
    getCaseList,
    getCaseDetail,
    currentData,
    getIssueListResult,
    getIssueGroupResult,
    updateIssueGroupResult,
    issueDetailObj,
    issueDiffObj,
    getNoticeListResult,
    noticeDetailObj,
    getCaseListResult,
    caseDetailObj,
    issueGroupSummary,
    result,
    setResult,
    executing,
    setExecuting,
    errObj,
    errMes,
    // unitname,
  };
};
